import React, { useState } from "react";
import FormField, { useTouched } from "../../create-case/components/party-involved/FormField";
import { relatedAsLabel } from "../../../api/creditAccounts";
import ManagementModal from "../../../components/ui/modals/ManagementModal";
import { RelatedAs } from "../../../api/stakeholder_relationships";

export interface EmployeePartyItem {
  partyId: string;
  name: string;
  types: RelatedAs[];
  employeeId?: string;
}

export interface EmployeeRowProps {
  type: string;
  name: string;
  employeeId?: string;
  showErrors?: boolean;
  onIdChange?: (value: string) => void;
}

export const EmployeeRow: React.FC<EmployeeRowProps> = ({
  type,
  name,
  employeeId = "",
  showErrors,
  onIdChange,
}) => {
  const { isTouched, markTouched } = useTouched();
  const error =
    (showErrors || isTouched("employeeId")) && !employeeId.trim()
      ? "Employee ID is required"
      : undefined;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
      <FormField
        type="text"
        label="Type"
        required
        placeholder="Select type"
        value={type}
        disabled
        onChange={() => {}}
      />
      <FormField
        type="text"
        label="Employee Name"
        required
        placeholder="Enter name"
        value={name}
        disabled
        onChange={() => {}}
      />
      <FormField
        type="text"
        label="Employee ID"
        required
        placeholder="Enter employee ID"
        value={employeeId}
        disabled={!onIdChange}
        error={onIdChange ? error : undefined}
        onChange={onIdChange ?? (() => {})}
        onBlur={() => markTouched("employeeId")}
      />
    </div>
  );
};

interface EmployeeModalProps {
  employees: EmployeePartyItem[];
  onClose: () => void;
  onBack?: () => void;
  onSave: (employeeIdMap: Record<string, string>) => Promise<void>;
}

const EmployeeModal: React.FC<EmployeeModalProps> = ({
  employees,
  onClose,
  onBack,
  onSave,
}) => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isValid = employees.every(
    (emp) => (values[emp.partyId] ?? "").trim().length > 0
  );

  const handleSave = async () => {
    setShowErrors(true);

    if (!isValid) {
      return;
    }

    setIsSaving(true);

    try {
      await onSave(values);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ManagementModal
      title="Employee Details"
      onClose={onClose}
      onBack={onBack}
      onCancel={onClose}
      onSubmit={handleSave}
      cancelLabel="Discard"
      submitLabel="Save"
      submitEnabled={isValid && !isSaving}
      isLoading={isSaving}
      className="w-[932px] max-w-[80vw]"
    >
      <div className="flex flex-col gap-4">
        {employees.map((emp) => {
          const typeLabel = emp.types.map((v) => relatedAsLabel(v)).join(", ");

          return (
            <EmployeeRow
              key={emp.partyId}
              type={typeLabel}
              name={emp.name}
              employeeId={values[emp.partyId] ?? ""}
              showErrors={showErrors}
              onIdChange={(val) =>
                setValues((prev) => ({ ...prev, [emp.partyId]: val }))
              }
            />
          );
        })}
      </div>
    </ManagementModal>
  );
};

export default EmployeeModal;